import { useCallback, useSyncExternalStore } from "react";

import { useAuthState } from "igfd-commons/src/auth";

/**
 * What each signed-in person has seen or closed. Kept per person in this
 * browser: one manager closing a tip doesn't hide it for their colleagues.
 */
export type GuestMenuNudge =
  | "guestMenuOpened"
  | "menusNoticeClosed"
  | "liveCalloutShown"
  | "keptClassic";

export const LIVE_CALLOUT_LIMIT = 3;
export const NOTICE_CLOSED_DAYS = 10;
export const KEPT_CLASSIC_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whether something hidden at the stored time is still hidden `now`. */
export const isSnoozed = (stored: string | null, days: number, now: number) => {
  const hiddenAt = Number(stored);
  return hiddenAt > 0 && now - hiddenAt < days * DAY_MS;
};

export const nudgeKey = (nudge: GuestMenuNudge, uid: string | undefined) =>
  `igfd.guestMenu.${nudge}.${uid ?? "signedOut"}`;

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const read = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // It shows again next visit; nothing else depends on it.
  }
  listeners.forEach((listener) => listener());
};

const useNudgeValue = (nudge: GuestMenuNudge) => {
  const { user } = useAuthState();
  const key = nudgeKey(nudge, user?.uid);
  const value = useSyncExternalStore(subscribe, () => read(key));
  const set = useCallback((next: string) => write(key, next), [key]);
  return [value, set] as const;
};

/** Whether this person has done it, and a way to remember they have. */
export const useNudgeDone = (nudge: GuestMenuNudge) => {
  const [value, set] = useNudgeValue(nudge);
  const markDone = useCallback(() => set("true"), [set]);
  return [value === "true", markDone] as const;
};

/** Whether this person hid it in the last `days`, and a way to hide it now. */
export const useNudgeSnoozed = (nudge: GuestMenuNudge, days: number) => {
  const [value, set] = useNudgeValue(nudge);
  const snooze = useCallback(() => set(String(Date.now())), [set]);
  return [isSnoozed(value, days, Date.now()), snooze] as const;
};

/** How many times this person has seen it, and a way to count one more. */
export const useNudgeCount = (nudge: GuestMenuNudge) => {
  const [value, set] = useNudgeValue(nudge);
  const count = Number(value) || 0;
  const countOne = useCallback(() => set(String(count + 1)), [count, set]);
  return [count, countOne] as const;
};
